import { useCallback, useEffect, useMemo } from 'react';
import { useRevitParametersStore } from '../../../shared/stores/useRevitParametersStore';
import { useSampledParameterNamesStore } from '../../../shared/stores/useSampledParameterNamesStore';
import { traceMappingFlow } from '../lib/mappingTrace';
import type { ProjectScope } from '../lib/mappingRepairProgress';
import type { AvailableProjectParameterDescriptor } from '../../../shared/lib/revit/projectParameterMutationPlan';
import { requiresMappedProjectInstanceBinding } from '../../../shared/lib/hooks/parameterAddition/mappedProjectParameterModePolicy';

export interface MappingCatalogRefreshSuspensionRef {
  readonly current: {
    active: boolean;
    projectKey: string | null;
  };
}

export function getReadinessParameterNames(
  parameters: Array<{ name: string; label: string; parameterType: 'Type' | 'Instance' }>
): string[] {
  return parameters.flatMap((parameter) => {
    if (
      requiresMappedProjectInstanceBinding(parameter.name) &&
      parameter.parameterType !== 'Instance'
    ) {
      return [];
    }
    return [parameter.name, parameter.label];
  });
}

export function waitForProgressWindowRequestEffect(): Promise<void> {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, 0);
  });
}

export async function fetchSampledMappingParameterNames(
  projectKey?: string | null,
  modelVersion?: number | null
): Promise<string[]> {
  return useSampledParameterNamesStore.getState().fetchNames({
    projectKey,
    modelVersion,
    force: true,
  });
}

export function matchesScope(scope: ProjectScope, projectKey: string | null, modelVersion: number | null): boolean {
  return scope.projectKey === projectKey && scope.modelVersion === modelVersion;
}

function toProjectParameterDescriptor(parameter: {
  name: string;
  dataType: string;
  parameterType: 'Type' | 'Instance';
  group: string;
  categories?: string[];
  isProjectBinding?: boolean;
}): AvailableProjectParameterDescriptor {
  return {
    name: parameter.name,
    dataType: parameter.dataType,
    parameterType: parameter.parameterType,
    group: parameter.group,
    categories: parameter.categories,
    isProjectBinding: parameter.isProjectBinding,
  };
}

export function useProjectParameterCatalog(input: {
  enabled: boolean;
  hostRuntimeAvailable: boolean;
  projectKey: string | null;
  modelVersion: number | null;
  refreshSuspendedRef?: MappingCatalogRefreshSuspensionRef;
}) {
  const { enabled, hostRuntimeAvailable, projectKey, modelVersion, refreshSuspendedRef } = input;
  const parameters = useRevitParametersStore((state) => state.parameters);
  const loadedProjectKey = useRevitParametersStore((state) => state.projectKey);
  const loadedModelVersion = useRevitParametersStore((state) => state.modelVersion);
  const isLoading = useRevitParametersStore((state) => state.isLoading);
  const isLoaded = useRevitParametersStore((state) => state.isLoaded);
  const error = useRevitParametersStore((state) => state.error);
  const fetchParameters = useRevitParametersStore((state) => state.fetchParameters);

  useEffect(() => {
    if (refreshSuspendedRef?.current.active && refreshSuspendedRef.current.projectKey === projectKey) {
      return;
    }
    if (!enabled || !projectKey || !hostRuntimeAvailable) {
      if (enabled) {
        traceMappingFlow('project parameter catalog fetch skipped', {
          projectKey,
          modelVersion,
          hostRuntimeAvailable,
          reason: !projectKey ? 'missingProjectKey' : 'hostRuntimeUnavailable',
        });
      }
      return;
    }

    const state = useRevitParametersStore.getState();
    traceMappingFlow('project parameter catalog fetch', {
      projectKey,
      modelVersion,
      force: false,
      loadedProjectKey: state.projectKey,
      loadedModelVersion: state.modelVersion,
      isLoading: state.isLoading,
      isLoaded: state.isLoaded,
      error: state.error,
      parameterCount: state.parameters.length,
    });
    void fetchParameters({
      projectKey,
      modelVersion,
    }).then(() => {
      const state = useRevitParametersStore.getState();
      traceMappingFlow('project parameter catalog fetch complete', {
        projectKey,
        modelVersion,
        force: false,
        loadedProjectKey: state.projectKey,
        loadedModelVersion: state.modelVersion,
        isLoaded: state.isLoaded,
        isLoading: state.isLoading,
        error: state.error,
        parameterCount: state.parameters.length,
      });
    });
  }, [
    enabled,
    fetchParameters,
    hostRuntimeAvailable,
    loadedModelVersion,
    loadedProjectKey,
    modelVersion,
    projectKey,
    refreshSuspendedRef,
  ]);

  const matchesProject = matchesScope(
    { projectKey: loadedProjectKey, modelVersion: loadedModelVersion },
    projectKey,
    modelVersion
  );
  const parameterNames = useMemo(
    () => matchesProject ? getReadinessParameterNames(parameters) : [],
    [matchesProject, parameters]
  );
  const descriptors = useMemo(
    () =>
      matchesProject
        ? parameters
            .filter((parameter) => Boolean(parameter.name))
            .map(toProjectParameterDescriptor)
        : [],
    [matchesProject, parameters]
  );

  const refetch = useCallback(async () => {
    if (!projectKey || !hostRuntimeAvailable) {
      traceMappingFlow('project parameter catalog refetch skipped', {
        projectKey,
        modelVersion,
        hostRuntimeAvailable,
        reason: !projectKey ? 'missingProjectKey' : 'hostRuntimeUnavailable',
      });
      return { parameterNames: [], descriptors: [] };
    }

    traceMappingFlow('project parameter catalog fetch', {
      projectKey,
      modelVersion,
      force: true,
    });
    await fetchParameters({
      projectKey,
      modelVersion,
      force: true,
    });
    const state = useRevitParametersStore.getState();
    traceMappingFlow('project parameter catalog refetch complete', {
      projectKey,
      modelVersion,
      loadedProjectKey: state.projectKey,
      loadedModelVersion: state.modelVersion,
      isLoaded: state.isLoaded,
      isLoading: state.isLoading,
      error: state.error,
      parameterCount: state.parameters.length,
    });
    if (!matchesScope(state, projectKey, modelVersion)) {
      return { parameterNames: [], descriptors: [] };
    }

    return {
      parameterNames: getReadinessParameterNames(state.parameters),
      descriptors: state.parameters
        .filter((parameter) => Boolean(parameter.name))
        .map(toProjectParameterDescriptor),
    };
  }, [fetchParameters, hostRuntimeAvailable, modelVersion, projectKey]);

  return {
    parameterNames,
    descriptors,
    isLoading,
    isLoaded,
    error,
    matchesProject,
    refetch,
  };
}

export function useSampledMappingCatalog(input: {
  enabled: boolean;
  runtimeAvailable: boolean;
  projectKey: string | null;
  modelVersion: number | null;
  refreshSuspendedRef?: MappingCatalogRefreshSuspensionRef;
}) {
  const { enabled, runtimeAvailable, projectKey, modelVersion, refreshSuspendedRef } = input;
  const names = useSampledParameterNamesStore((state) => state.names);
  const storeIsLoading = useSampledParameterNamesStore((state) => state.isLoading);
  const attempted = useSampledParameterNamesStore((state) => state.attempted);
  const error = useSampledParameterNamesStore((state) => state.error);
  const loadedProjectKey = useSampledParameterNamesStore((state) => state.projectKey);
  const loadedModelVersion = useSampledParameterNamesStore((state) => state.modelVersion);
  const fetchNames = useSampledParameterNamesStore((state) => state.fetchNames);

  useEffect(() => {
    if (refreshSuspendedRef?.current.active && refreshSuspendedRef.current.projectKey === projectKey) {
      return;
    }
    if (!enabled || !projectKey || !runtimeAvailable) {
      return;
    }

    // The catalog store is shared with the profile gate matching, so this
    // joins the in-flight enumeration instead of paying for a second one.
    void fetchNames({ projectKey, modelVersion }).catch(() => {
      // Fetch failures are recorded in the store and surface through isReady/error.
    });
  }, [
    enabled,
    fetchNames,
    loadedModelVersion,
    loadedProjectKey,
    modelVersion,
    projectKey,
    refreshSuspendedRef,
    runtimeAvailable,
  ]);

  const matchesProject = matchesScope(
    { projectKey: loadedProjectKey, modelVersion: loadedModelVersion },
    projectKey,
    modelVersion
  );
  const isReady =
    !runtimeAvailable ||
    (matchesProject && (attempted || error !== null));
  const isLoading = runtimeAvailable && matchesProject && storeIsLoading;

  return {
    names: matchesProject ? names : [],
    isLoading,
    isReady,
  };
}
